import React from 'react';
import { isStaleBuildError } from '../lib/staleBuildGuard';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      lastErrorTime: null,
      isChunkLoadError: false,
    };
    console.log('[ErrorBoundary] Constructor');
  }

  static getDerivedStateFromError(error) {
    console.log('[ErrorBoundary] getDerivedStateFromError:', error);

    // IGNORE sign-out related errors - these are expected and normal
    if (error && error.message) {
      const errorMessage = error.message.toLowerCase();
      const isSignOutError =
        errorMessage.includes('auth session missing') ||
        errorMessage.includes('session not found') ||
        errorMessage.includes('user not found') ||
        errorMessage.includes('invalid session') ||
        errorMessage.includes('no session') ||
        errorMessage.includes('session expired') ||
        errorMessage.includes('signout') ||
        errorMessage.includes('sign out') ||
        errorMessage.includes('logout') ||
        errorMessage.includes('log out');

      if (isSignOutError) {
        console.log('[ErrorBoundary] Ignoring sign-out related error:', errorMessage);
        // Don't trigger error boundary for sign-out errors
        return { hasError: false };
      }
    }

    // Update state so the next render will show the fallback UI for other errors
    return {
      hasError: true,
      error,
      lastErrorTime: Date.now(),
      isChunkLoadError: isStaleBuildError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    // Check if this is a sign-out error that should be ignored
    if (error && error.message) {
      const errorMessage = error.message.toLowerCase();
      const isSignOutError =
        errorMessage.includes('auth session missing') ||
        errorMessage.includes('session not found') ||
        errorMessage.includes('user not found') ||
        errorMessage.includes('invalid session') ||
        errorMessage.includes('no session') ||
        errorMessage.includes('session expired') ||
        errorMessage.includes('signout') ||
        errorMessage.includes('sign out') ||
        errorMessage.includes('logout') ||
        errorMessage.includes('log out');

      if (isSignOutError) {
        console.log('[ErrorBoundary] componentDidCatch: Ignoring sign-out related error:', errorMessage);
        return; // Don't process sign-out errors
      }
    }

    // Enhanced error logging with more context for non-signout errors
    console.error('[ErrorBoundary] Caught an error:', error, errorInfo);

    // Log additional context
    console.error('[ErrorBoundary] Error details:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });

    this.setState({ errorInfo: errorInfo });

    // Clear any problematic session data that might be causing crashes
    try {
      const keysToCheck = [
        'ai_agent_session_AQA',
        'ai_agent_session_OCR',
        'ai_agent_session_Edexcel'
      ];

      keysToCheck.forEach(key => {
        const sessionData = sessionStorage.getItem(key);
        if (sessionData) {
          try {
            JSON.parse(sessionData);
          } catch (parseError) {
            console.warn(`[ErrorBoundary] Removing corrupted session data for key: ${key}`);
            sessionStorage.removeItem(key);
          }
        }
      });
    } catch (cleanupError) {
      console.warn('[ErrorBoundary] Error during session cleanup:', cleanupError);
    }
  }

  handleRetry = () => {
    console.log('[ErrorBoundary] Retry button clicked');

    // Clear error state and increment retry count
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
        isChunkLoadError: false,
      }));

    // Clear potentially problematic data (session data that survives reload
    // but gets cleared on sign out — the root cause of post-deploy crashes)
    try {
      const sessionKeys = Object.keys(sessionStorage).filter(key =>
        key.startsWith('ai_agent_session_') ||
        key.startsWith('ai_agent_mode_') ||
        key === 'ai_session_active'
      );
      sessionKeys.forEach(key => sessionStorage.removeItem(key));

      if (window.responseCache) {
        window.responseCache.clear();
      }
    } catch (cleanupError) {
      console.warn('[ErrorBoundary] Error during retry cleanup:', cleanupError);
    }
  };

  handleReload = () => {
    console.log('[ErrorBoundary] Reload button clicked');
    // Clear ALL sessionStorage — mimics the cleanup that sign-out does.
    // This fixes crashes caused by stale auth tokens, corrupted session data,
    // or version-mismatched state that survives normal reloads.
    try {
      sessionStorage.clear();
    } catch (e) {
      // Ignore - best effort cleanup
    }
    // Force fresh navigation (bypasses bfcache and ensures fresh HTML)
    window.location.replace(window.location.pathname);
  };

  render() {
    if (this.state.hasError) {
      console.log('[ErrorBoundary] Rendering fallback UI');

      const canRetry = this.state.retryCount < 3; // Allow up to 3 retries
      const timeSinceError = Date.now() - (this.state.lastErrorTime || 0);
      const shouldShowRetry = canRetry && timeSinceError > 2000; // Wait 2 seconds before showing retry
      const title = this.state.isChunkLoadError ? 'Updating the app' : 'Something went wrong';
      const description = this.state.isChunkLoadError
        ? 'A new version was just deployed. We are refreshing the app to pull the latest files.'
        : 'We encountered an unexpected error. This is usually temporary and can be fixed by trying again.';

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-800 p-8 font-sans">
          <div className="text-center max-w-lg bg-white rounded-3xl p-10 border border-slate-200 shadow-xl shadow-slate-200/50">
            {/* Error Icon */}
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-rose-100 rounded-full flex items-center justify-center shadow-inner">
                <svg className="w-10 h-10 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>

            <h1 className="text-4xl font-extrabold mb-4 text-slate-900 tracking-tight">{title}</h1>
            <p className="mb-8 text-slate-600 font-medium leading-relaxed text-lg">
              {description}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              {shouldShowRetry && (
                <button
                  onClick={this.handleRetry}
                  className="px-6 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all duration-300 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg shadow-emerald-500/20"
                >
                  Try Again ({3 - this.state.retryCount} attempts left)
                </button>
              )}
              <button
                onClick={this.handleReload}
                className="px-6 py-3.5 bg-white border-2 border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-700 font-bold rounded-xl transition-all duration-300 transform hover:-translate-y-0.5 shadow-sm"
              >
                Refresh Page
              </button>
              <a
                href="/"
                className="px-6 py-3.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 font-medium rounded-xl transition-all duration-300"
              >
                Go Home
              </a>
            </div>

            {/* Status Info */}
            {this.state.retryCount > 0 && (
              <p className="text-sm font-bold text-rose-500 bg-rose-50 inline-block px-3 py-1 rounded-full mb-4">
                Retry attempts: {this.state.retryCount}/3
              </p>
            )}

            {/* Error Details — temporarily visible in production to diagnose crash */}
            {this.state.error && (
              <details className="mt-6 text-left bg-slate-50 p-6 rounded-2xl border border-slate-200" open>
                <summary className="cursor-pointer text-slate-700 font-bold mb-4 outline-none">Error Details</summary>
                <div className="mt-4 space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2">Error Message:</p>
                    <pre className="text-xs text-rose-600 bg-white p-4 rounded-xl border border-rose-100 overflow-auto shadow-inner">
                      {this.state.error.message}
                    </pre>
                  </div>
                  {this.state.error.stack && (
                    <div>
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2">Stack Trace:</p>
                      <pre className="text-xs text-slate-700 bg-white p-4 rounded-xl border border-slate-200 overflow-auto whitespace-pre-wrap max-h-48 shadow-inner">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                  {this.state.errorInfo && this.state.errorInfo.componentStack && (
                    <div>
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2">Component Stack:</p>
                      <pre className="text-xs text-slate-700 bg-white p-4 rounded-xl border border-slate-200 overflow-auto whitespace-pre-wrap max-h-48 shadow-inner">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Help Text */}
            <div className="mt-8 pt-6 border-t border-slate-100 text-sm font-medium text-slate-500">
              <p className="mb-3 font-bold text-slate-700">If this problem persists, please try:</p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center justify-center gap-2"><svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg> Clearing your browser cache</li>
                <li className="flex items-center justify-center gap-2"><svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg> Using an incognito/private window</li>
                <li className="flex items-center justify-center gap-2"><svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg> Checking your internet connection</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    // console.log('[ErrorBoundary] Rendering children');
    return this.props.children;
  }
}

export default ErrorBoundary; 
